import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  AlertTriangle,
  Ban,
  CheckCircle2,
  FileText,
  Link as LinkIcon,
  List,
  MessageSquare,
  Scale,
  Sliders,
  Star,
} from 'lucide-react';

import { AttentionBorder } from '@/components/shared/AttentionBorder';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import type { EnrichmentCardId } from '@/lib/enrichment';
import { cardFilled } from '@/lib/enrichment';
import { cn } from '@/lib/utils';
import type { KbProfile } from '@/services/kbProfileService';

import { DifferentiatorsEditor } from './editors/DifferentiatorsEditor';
import { FaqEditor } from './editors/FaqEditor';
import { LimitsEditor } from './editors/LimitsEditor';
import { LinksEditor } from './editors/LinksEditor';
import { PoliciesEditor } from './editors/PoliciesEditor';
import { ProactiveNoticeEditor } from './editors/ProactiveNoticeEditor';
import { RestrictionsEditor } from './editors/RestrictionsEditor';
import { SlotsEditor } from './editors/SlotsEditor';
import { VoiceEditor } from './editors/VoiceEditor';

const CARD_ICONS: Record<EnrichmentCardId, React.ComponentType<{ className?: string }>> = {
  differentiators: Star,
  voice: MessageSquare,
  faq: List,
  restrictions: Ban,
  limits: Scale,
  slots: Sliders,
  links: LinkIcon,
  policies: FileText,
  proactive_notice: AlertTriangle,
};

interface EnrichmentCardProps {
  id: EnrichmentCardId;
  profile?: KbProfile;
  recommended: boolean;
  disabled?: boolean;
}

export function EnrichmentCard({
  id,
  profile,
  recommended,
  disabled = false,
}: EnrichmentCardProps) {
  const { t } = useTranslation();
  const [dialogOpen, setDialogOpen] = useState(false);

  const Icon = CARD_ICONS[id];
  const filled = !disabled && profile ? cardFilled(profile, id) : false;

  return (
    <AttentionBorder active={recommended && !disabled}>
      <Card
        data-testid={`enrichment-card-${id}`}
        aria-disabled={disabled || undefined}
        className={cn(
          'flex flex-col transition-colors',
          disabled ? 'opacity-60' : 'hover:border-foreground/20'
        )}
      >
        <CardContent className="flex flex-col gap-3 p-4 flex-1">
          {/* Header row */}
          <div className="flex items-start justify-between gap-2">
            <div className="flex min-w-0 items-center gap-2">
              <Icon className="h-4 w-4 text-muted-foreground shrink-0" aria-hidden="true" />
              <span className="text-sm font-medium leading-tight">
                {t(`bot.enrich.${id}.title`)}
              </span>
            </div>
            <div className="flex items-center gap-1 shrink-0">
              {recommended && !disabled && (
                <Badge variant="secondary" className="text-xs px-1.5 py-0.5 leading-none">
                  {t('bot.enrich.recommended_pill')}
                </Badge>
              )}
              {filled && (
                <CheckCircle2
                  className="h-4 w-4 text-success shrink-0"
                  aria-label={t('bot.enrich.filled_label')}
                />
              )}
            </div>
          </div>

          {/* Description */}
          <p className="text-xs text-muted-foreground leading-relaxed">
            {t(`bot.enrich.${id}.description`)}
          </p>

          {/* F193 — Voice card summary: only surfaces when the tenant chose
              the non-default 'short' welcome; 'long' (default) says nothing
              extra, matching every other card's silent-default pattern. */}
          {id === 'voice' && !disabled && profile?.welcome_style === 'short' && (
            <p className="text-xs text-muted-foreground">{t('bot.enrich.voice.summary_short')}</p>
          )}

          {/* Locked hint — only when disabled */}
          {disabled && (
            <p className="text-xs text-muted-foreground">{t('bot.enrich.locked_hint')}</p>
          )}

          {/* CTA */}
          <Button
            variant="outline"
            size="sm"
            className="mt-auto min-h-11 w-full"
            onClick={() => !disabled && setDialogOpen(true)}
            disabled={disabled}
            data-testid={`enrichment-card-${id}-cta`}
          >
            {filled ? t('bot.enrich.edit_cta') : t(`bot.enrich.${id}.cta`)}
          </Button>
        </CardContent>
      </Card>

      {/* Per-card editor dialogs — only mounted when not disabled AND profile is available */}
      {!disabled && profile && (
        <>
          {id === 'differentiators' && (
            <DifferentiatorsEditor
              open={dialogOpen}
              onOpenChange={setDialogOpen}
              initialValue={profile.differentiators}
              initialTeamCredentials={profile.team_credentials}
            />
          )}
          {id === 'voice' && (
            <VoiceEditor
              open={dialogOpen}
              onOpenChange={setDialogOpen}
              initialTone={profile.tone}
              initialVoiceExample={profile.voice_example}
              initialWelcomeStyle={profile.welcome_style}
            />
          )}
          {id === 'faq' && (
            <FaqEditor open={dialogOpen} onOpenChange={setDialogOpen} initialFaq={profile.faq} />
          )}
          {id === 'restrictions' && (
            <RestrictionsEditor
              open={dialogOpen}
              onOpenChange={setDialogOpen}
              initialRestrictions={profile.restrictions}
            />
          )}
          {id === 'limits' && (
            <LimitsEditor
              open={dialogOpen}
              onOpenChange={setDialogOpen}
              initialForbiddenScope={profile.forbidden_scope}
              initialDisclaimer={profile.persistent_disclaimer}
              initialNeverSay={profile.never_say}
              initialReferExternalDestination={profile.refer_external_destination}
              initialForbiddenScopeNotify={profile.forbidden_scope_notify ?? false}
            />
          )}
          {id === 'slots' && (
            <SlotsEditor
              open={dialogOpen}
              onOpenChange={setDialogOpen}
              initialSlots={profile.slot_definitions}
            />
          )}
          {id === 'links' && (
            <LinksEditor
              open={dialogOpen}
              onOpenChange={setDialogOpen}
              initialLinks={profile.link_library}
            />
          )}
          {id === 'policies' && (
            <PoliciesEditor
              open={dialogOpen}
              onOpenChange={setDialogOpen}
              initialValue={profile.policies}
            />
          )}
          {id === 'proactive_notice' && (
            <ProactiveNoticeEditor
              open={dialogOpen}
              onOpenChange={setDialogOpen}
              initialValue={profile.proactive_notice ?? null}
            />
          )}
        </>
      )}
    </AttentionBorder>
  );
}
